/**
 * Free Shipping Bar — Web Component
 */
class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 10000;
    this.update();
    document.addEventListener('cart:update', () => this.update());
  }

  update() {
    const totalCents = parseInt(this.dataset.cartTotal, 10) || 0;
    const remaining = Math.max(0, this.threshold - totalCents);
    const progress = Math.min(100, (totalCents / this.threshold) * 100);

    const fill = this.querySelector('.cart-drawer__shipping-fill');
    const text = this.querySelector('.cart-drawer__shipping-text');
    const indicator = this.querySelector('.cart-drawer__shipping-indicator');

    if (fill) fill.style.width = `${progress}%`;
    if (indicator) indicator.style.display = progress > 0 ? 'block' : 'none';

    if (text) {
      if (remaining <= 0) {
        text.textContent = this.dataset.messageFree || 'Free shipping included';
      } else {
        const formattedAmount = this.formatMoney(remaining);
        const template = this.dataset.messageRemaining || 'Spend {amount} more to get free shipping';
        text.textContent = template.replace('{amount}', formattedAmount);
      }
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}

/**
 * Cart Drawer Recommendations — Web Component
 */
class CartDrawerRecommendations extends HTMLElement {
  #controller = null;
  #cartUpdateHandler = null;
  #loaded = false;
  #dialogObserver = null;
  #mutationObserver = null;

  connectedCallback() {
    this.loadRecommendations();

    this.#cartUpdateHandler = () => {
      // El tema re-renderiza el drawer después del cart:update,
      // esperamos a que termine antes de volver a pintar
      setTimeout(() => this.loadRecommendations(), 300);
    };
    document.addEventListener('cart:update', this.#cartUpdateHandler);

    // Observar si el grid queda vacío después de un re-render del tema
    this.#mutationObserver = new MutationObserver(() => {
      const grid = this.querySelector('.cart-drawer__recommendations-grid');
      if (grid && grid.children.length === 0 && this.#loaded) {
        this.loadRecommendations();
      }
    });
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    const dialog = this.closest('dialog');
    if (dialog) {
      this.#dialogObserver = new MutationObserver(() => {
        if (dialog.open && !this.#loaded) {
          this.loadRecommendations();
        }
      });
      this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:update', this.#cartUpdateHandler);
      this.#cartUpdateHandler = null;
    }
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
  }

  async loadRecommendations() {
    const productId = this.dataset.productId;
    const fallbackCollection = this.dataset.fallbackCollection;

    if (!productId && !fallbackCollection) {
      this.style.display = 'none';
      return;
    }

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const limit = parseInt(this.dataset.limit, 10) || 4;
      let products = [];

      if (productId) {
        const url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`;
        const response = await fetch(url, { signal: this.#controller.signal });
        if (response.ok) {
          const data = await response.json();
          products = data.products || [];
        }
      }

      if (products.length === 0 && fallbackCollection) {
        const collectionUrl = `${window.Shopify.routes.root}collections/${fallbackCollection}/products.json?limit=${limit}`;
        const collectionResponse = await fetch(collectionUrl, { signal: this.#controller.signal });
        if (collectionResponse.ok) {
          const collectionData = await collectionResponse.json();
          products = collectionData.products || [];
        }
      }

      if (products.length === 0) {
        this.style.display = 'none';
        return;
      }

      requestAnimationFrame(() => {
        this.renderProducts(products);
        this.style.display = '';
        this.#loaded = true;
      });

    } catch (e) {
      if (e.name !== 'AbortError') {
        this.style.display = 'none';
      }
    }
  }

  getSizedImageUrl(url, width) {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}width=${width}`;
  }

  formatPrice(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    if (window.Shopify?.currency?.active) {
      return `$${amount} ${window.Shopify.currency.active}`;
    }
    return `$${amount}`;
  }

  renderProducts(products) {
    const grid = this.querySelector('.cart-drawer__recommendations-grid');
    if (!grid) return;

    grid.innerHTML = products.map((product) => {
      const rawImage = product.featured_image
        ? (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image.src || product.featured_image)
        : product.images?.[0]?.src || product.images?.[0] || '';
      const image = this.getSizedImageUrl(rawImage, 186);
      const variant = product.variants?.[0];
      const variantId = variant?.id || '';
      const price = variant ? this.formatPrice(variant.price) : '$0.00';

      return `
        <div class="cart-drawer__rec-card">
          <div class="cart-drawer__rec-card-image">
            <a href="${product.url}">
              <img
                src="${image}"
                alt="${this.escapeHtml(product.title)}"
                width="93"
                height="124"
                loading="lazy"
              >
            </a>
            <button
              class="cart-drawer__rec-card-atc"
              type="button"
              data-variant-id="${variantId}"
              aria-label="Add ${this.escapeHtml(product.title)} to cart"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/>
                <path stroke="currentColor" stroke-linecap="round" stroke-width="1.2" d="M14.608 12.563v5m2.5-2.5h-5"/>
              </svg>
            </button>
          </div>
          <div class="cart-drawer__rec-card-info">
            <p class="cart-drawer__rec-card-title">${this.escapeHtml(product.title)}</p>
            <p class="cart-drawer__rec-card-price">${price}</p>
          </div>
        </div>
      `;
    }).join('');

    grid.querySelectorAll('.cart-drawer__rec-card-atc').forEach((btn) => {
      btn.addEventListener('click', (e) => this.addToCart(e));
    });
  }

  async addToCart(e) {
    const btn = e.currentTarget;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.disabled = true;

    try {
      const addResponse = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] }),
      });

      if (!addResponse.ok) return;

      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      const itemCount = cart?.item_count ?? 0;

      const sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
      let sections = undefined;
      if (sectionId) {
        const sectionsUrl = `${window.location.pathname}?sections=${sectionId}`;
        const sectionsResp = await fetch(sectionsUrl);
        if (sectionsResp.ok) {
          sections = await sectionsResp.json();
        }
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: 'cart-drawer-recommendations',
            data: {
              itemCount,
              source: 'cart-drawer-recommendations',
              sections,
            },
          },
        })
      );
    } catch (err) {
      console.error('Add to cart failed:', err);
    } finally {
      btn.disabled = false;
    }
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}

/**
 * Selling Plan Change — handles frequency dropdown & one-time switch in cart drawer
 */
let _sellingPlanChanging = false;

document.addEventListener('change', async (e) => {
  const select = e.target?.closest?.('.cart-items__selling-plan-select');
  if (!select) return;

  // Prevent re-trigger loop from drawer re-render resetting the dropdown
  if (_sellingPlanChanging) {
    return;
  }

  const lineKey = select.dataset.lineKey;
  const variantId = Number(select.dataset.variantId);
  const quantity = Number(select.dataset.quantity) || 1;
  const newSellingPlan = Number(select.value);

  if (!lineKey || !variantId) return;

  _sellingPlanChanging = true;
  select.disabled = true;

  // Build the new cart item — omit selling_plan for one-time purchase
  const newItem = { id: variantId, quantity };
  if (newSellingPlan > 0) {
    newItem.selling_plan = newSellingPlan;
  }

  try {
    // 1. Remove old item
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });

    // 2. Add new item with updated selling plan (or none)
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [newItem] }),
    });

    // 3. Get the section ID from the cart-items-component
    const cartItemsEl = select.closest('cart-items-component');
    const sectionId = cartItemsEl?.dataset?.sectionId;

    if (sectionId) {
      // 4. Fetch fresh section HTML via Section Rendering API
      const sectionsUrl = `${window.location.pathname}?sections=${sectionId}`;
      const sectionsResp = await fetch(sectionsUrl);
      const sections = await sectionsResp.json();
      const freshHTML = sections[sectionId];

      if (freshHTML) {
        // 5. Parse and replace the cart drawer inner content
        const parsed = new DOMParser().parseFromString(freshHTML, 'text/html');
        const newInner = parsed.querySelector('[data-hydration-key="cart-drawer-inner"]');
        const currentInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');

        if (newInner && currentInner) {
          currentInner.innerHTML = newInner.innerHTML;
        }
      }

      // 6. Dispatch cart:update so other components (shipping bar, bubble, etc.) refresh
      const cartResp = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = cartResp.ok ? await cartResp.json() : null;

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            data: {
              itemCount: cart?.item_count ?? 0,
              source: 'selling-plan-change',
              sections,
            },
          },
        })
      );
    }
  } catch (err) {
    console.error('[Cart] Selling plan change failed:', err);
    select.disabled = false;
  } finally {
    setTimeout(() => { _sellingPlanChanging = false; }, 500);
  }
});